// Tauri IPC wrappers.
//
// We don't call invoke() inline anywhere — the wrappers below are the only
// surface that touches @tauri-apps/api. That keeps the rest of the app
// testable in a plain browser dev context: when window.__TAURI_INTERNALS__
// isn't present, the wrappers fall back to synthetic data so `vite` alone
// renders a usable UI.

import type { CohortBundle, HealthSnapshot } from "./types";

const hasTauri = typeof window !== "undefined" && "__TAURI_INTERNALS__" in window;

async function lazyInvoke<T>(cmd: string, args?: Record<string, unknown>): Promise<T> {
  // Dynamic import so plain-Vite builds don't try to resolve Tauri internals.
  const mod = await import("@tauri-apps/api/core");
  return mod.invoke<T>(cmd, args);
}

export async function loadConfig(): Promise<Record<string, unknown>> {
  if (!hasTauri) {
    // Browser dev: persist to localStorage instead of disk.
    const raw = localStorage.getItem("bvndle-cc:config");
    return raw ? JSON.parse(raw) : {};
  }
  return lazyInvoke<Record<string, unknown>>("load_config");
}

export async function saveConfig(value: Record<string, unknown>): Promise<void> {
  if (!hasTauri) {
    localStorage.setItem("bvndle-cc:config", JSON.stringify(value));
    return;
  }
  await lazyInvoke<void>("save_config", { value });
}

export async function readCohorts(): Promise<CohortBundle> {
  if (!hasTauri) {
    // Browser dev: fetch from /resources/cohorts (Vite serves /public).
    return { data: {}, errors: { _dev: "Tauri not detected — cohorts unavailable in browser dev" } };
  }
  return lazyInvoke<CohortBundle>("read_cohorts");
}

export async function probeHealth(): Promise<HealthSnapshot> {
  if (!hasTauri) {
    // Synthetic dev fallback — mirrors the Rust synthetic for parity.
    return {
      timestamp: new Date().toISOString(),
      invariant_holds: true,
      bus_ok: true,
      durable_pg: { status: "ok", detail: null },
      hot_redis: { status: "ok", detail: null },
      last_block: "14:42:07",
      replica_lag_ms: 2,
      surfaces: [
        { id: "whatsapp", label: "WhatsApp", status: "live", vu: 6, primary_metric: "2.1k sess", secondary_metric: "▲18.4k MAU" },
        { id: "ussd", label: "USSD", status: "degraded", vu: 3, primary_metric: "280 ms", secondary_metric: "6.2k users" },
        { id: "pmint-app", label: "pMint App", status: "live", vu: 9, primary_metric: "31 tx/s", secondary_metric: "▲12.1k DAU" },
        { id: "pos", label: "POS", status: "live", vu: 5, primary_metric: "1,204 stores", secondary_metric: "▲R2.3M" },
        { id: "wa-business", label: "WA Business", status: "live", vu: 4, primary_metric: "86 sess", secondary_metric: "▲312 mrch" },
      ],
    };
  }
  return lazyInvoke<HealthSnapshot>("probe_health");
}

export const tauriIsAvailable = hasTauri;
